import React, { useState } from "react";
import { Box, Typography, TextField, Button, Autocomplete } from "@mui/material";
import API_BASE from "../config";
import { SERVICE_TYPE_OPTIONS } from "../constants/serviceTypes";
import { useSnackbar } from "../SnackbarContext";

function AddMaintenance({ onAdd, user, selectedCar, records }) {
  const { showError } = useSnackbar();
  const [serviceType, setServiceType] = useState("");
  const [mileage, setMileage] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [cost, setCost] = useState("");
  const [error, setError] = useState("");

  const maintenanceOptions = SERVICE_TYPE_OPTIONS;

  const currentMileage = selectedCar
    ? Math.max(0, ...records.map((rec) => Number(rec.mileage) || 0))
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    if (!selectedCar) {
      showError("Please select a car first.");
      return;
    }

    const newMileage = Number(mileage);
    if (Number.isNaN(newMileage)) {
      setError("Please enter a valid mileage number.");
      return;
    }

    if (newMileage < currentMileage) {
      setError(
        `Mileage must be ${currentMileage.toLocaleString()} mi or higher for this car.`
      );
      return;
    }

    let costValue = null;
    if (cost !== "") {
      costValue = Number(cost);
      if (Number.isNaN(costValue) || costValue < 0) {
        setError("Cost must be a valid non-negative number.");
        return;
      }
    }

    setError("");
    const token = await user.getIdToken();

    try {
      const response = await fetch(`${API_BASE}/add_record`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          serviceType,
          mileage,
          date,
          vehicleId: selectedCar.id,
          cost: costValue,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message = errorData?.error || "Failed to add record.";
        throw new Error(message);
      }

      const saved = await response.json();
      onAdd(saved);
      setError("");
      setServiceType("");
      setMileage("");
      setDate(new Date().toISOString().split("T")[0]);
      setCost("");
    } catch (err) {
      console.error("Error submitting record:", err);
      setError(err.message || "Unable to add record.");
    }
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" fontWeight={800} gutterBottom>
        Add Maintenance Record
      </Typography>
      <form onSubmit={handleSubmit}>
        <Autocomplete
          freeSolo
          fullWidth
          options={maintenanceOptions}
          value={serviceType}
          onChange={(e, value) => setServiceType(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Service Type"
              required
              margin="normal"
              fullWidth
            />
          )}
        />
        <TextField
          fullWidth
          label="Mileage"
          type="number"
          value={mileage}
          onChange={(e) => setMileage(e.target.value)}
          inputProps={{ min: currentMileage, max: 1000000 }}
          required
          margin="normal"
          error={Boolean(error)}
          helperText={
            error ||
            (currentMileage > 0
              ? `Must be ${currentMileage.toLocaleString()} mi or higher.`
              : "Enter the current mileage for this vehicle.")
          }
        />
        <TextField
          fullWidth
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: new Date().toISOString().split("T")[0] }}
          required
          margin="normal"
        />
        <TextField
          fullWidth
          label="Cost"
          placeholder="85.00"
          type="number"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          inputProps={{ min: 0, step: "0.01" }}
          margin="normal"
        />
        <Button fullWidth type="submit" variant="contained" color="primary" sx={{ marginTop: "16px" }}>
          Add Record
        </Button>
      </form>
    </Box>
  );
}

export default AddMaintenance;
